import { seeded } from "@mineoptics/brand/noise";
import { useReducedMotion } from "@/lib/motion";
import { useCanvas, type Frame } from "@mineoptics/brand/canvas/useCanvas";

// Typical cost-model inputs, with their units. Illustrative values.
const INPUTS = [
  "12 m", "229 mm", "0.35 kg/t", "1.15", "38 m/h", "85 %", "2.7 t/m³", "5 Mt/y", "6,600 h", "220 t",
  "4.2 km", "32 km/h", "24 km/h", "3.5 min", "$185/h", "92 %", "330 d", "2 × 10 h", "1.2 m", "$0.92/L",
  "2,400 t/h", "450 kW", "$0.11/kWh", "8 %", "10 y", "2.5 %", "92.5 %", "$4.2 M", "7 y", "34 m³",
];

interface Particle {
  label: string;
  /** -1..1, where the number enters at the top. */
  lane: number;
  speed: number;
  phase: number;
  size: number;
}

const PARTICLES: Particle[] = (() => {
  const random = seeded(23);
  const count = 48;
  return Array.from({ length: count }, (_, index) => ({
    label: INPUTS[index % INPUTS.length],
    // One lane each, shuffled in time, so labels rarely overlap.
    lane: ((index + random() * 0.8) / count) * 2 - 1,
    speed: 0.03 + random() * 0.018,
    phase: (index * 0.618 + random() * 0.1) % 1,
    size: 11 + Math.floor(random() * 4),
  }));
})();

interface Props {
  className?: string;
}

/** Inputs enter across the full width and fall into a single point: many assumptions, one answer. */
export function NumberFunnel({ className }: Props) {
  const reduced = useReducedMotion();

  const draw = ({ ctx, width, height, time }: Frame) => {
    ctx.clearRect(0, 0, width, height);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const centre = width / 2;
    const clock = reduced ? 9 : time;

    // Guide lines of the funnel.
    ctx.strokeStyle = "rgba(12, 15, 13, 0.1)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const lane of [-1, -0.5, 0, 0.5, 1]) {
      ctx.moveTo(centre + lane * centre * 0.96, 0);
      ctx.bezierCurveTo(centre + lane * centre * 0.96, height * 0.62, centre + lane * centre * 0.5, height * 0.86, centre, height);
    }
    ctx.stroke();

    for (const particle of PARTICLES) {
      const fall = (clock * particle.speed + particle.phase) % 1;
      // Stay wide at first, then narrow quickly: the shape of a funnel.
      const squeeze = 1 - Math.pow(fall, 2.1);
      const x = centre + particle.lane * centre * 0.92 * squeeze;
      const y = fall * height;
      const alpha = Math.min(1, fall * 7) * Math.min(1, (1 - fall) * 5);
      const size = particle.size * (0.62 + 0.38 * squeeze);

      ctx.font = `500 ${size.toFixed(1)}px "Geist Mono Variable", ui-monospace, monospace`;
      ctx.fillStyle = `rgba(12, 15, 13, ${(0.3 + 0.6 * squeeze) * alpha})`;
      ctx.fillText(particle.label, x, y);
    }
  };

  const ref = useCanvas(draw, { animate: !reduced, maxDpr: 2 });

  return <canvas ref={ref} aria-hidden="true" className={className} />;
}
